import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import AlertMessage from "components/UI/AlertMessage";
import Unauthorized from "components/UI/Unauthorized";
import useAlert from "hooks/useAlert";
import useAPI from "hooks/useAPI";
import { usePermissions } from "context/PermissionsContext";
import { useTranslation } from "react-i18next";
import {
  Card,
  Grid,
  Skeleton,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import DataTable from "react-data-table-component";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import TopicIcon from "@mui/icons-material/Topic";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import LayersIcon from "@mui/icons-material/Layers";
import MessageIcon from "@mui/icons-material/Message";
import SearchIcon from "@mui/icons-material/Search";
import colors from "assets/theme/base/colors";

// ─── helpers ─────────────────────────────────────────────────────────────────

const STATUS_META = {
  caught_up: { label: "Caught Up", color: "#2dce89", bg: "rgba(45,206,137,0.12)" },
  slight_lag: { label: "Slight Lag", color: "#fb6340", bg: "rgba(251,99,64,0.12)" },
  significant_lag: { label: "Significant Lag", color: "#f5365c", bg: "rgba(245,54,92,0.12)" },
  error: { label: "Error", color: "#f5365c", bg: "rgba(245,54,92,0.12)" },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.error;
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        background: meta.bg,
        color: meta.color,
        fontWeight: 700,
        fontSize: "0.72rem",
        height: 24,
        borderRadius: "6px",
        letterSpacing: 0.2,
      }}
    />
  );
};
StatusBadge.propTypes = { status: PropTypes.string.isRequired };

const StatCard = ({ icon, label, value, color, loading: isLoading }) => (
  <Card
    sx={{
      p: 2.5,
      display: "flex",
      alignItems: "center",
      gap: 2,
      border: "1px solid rgba(0,0,0,0.06)",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      borderRadius: "12px",
      height: "100%",
    }}
  >
    <SoftBox
      sx={{
        width: 48,
        height: 48,
        borderRadius: "12px",
        background: `linear-gradient(135deg, ${color}22 0%, ${color}44 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
    </SoftBox>
    <SoftBox>
      <SoftTypography variant="caption" color="secondary" sx={{ fontWeight: 500 }}>
        {label}
      </SoftTypography>
      <SoftTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.2, mt: 0.2 }}>
        {isLoading ? <Skeleton width={60} /> : value ?? "—"}
      </SoftTypography>
    </SoftBox>
  </Card>
);
StatCard.defaultProps = { value: null, loading: false };
StatCard.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  color: PropTypes.string.isRequired,
  loading: PropTypes.bool,
};

const LagChip = ({ lag }) => {
  const color = lag === 0 ? "#2dce89" : lag < 100 ? "#fb6340" : "#f5365c";
  const bg =
    lag === 0 ? "rgba(45,206,137,0.1)" : lag < 100 ? "rgba(251,99,64,0.1)" : "rgba(245,54,92,0.1)";
  return (
    <Chip
      label={lag.toLocaleString()}
      size="small"
      sx={{
        background: bg,
        color,
        fontWeight: 700,
        fontSize: "0.72rem",
        height: 22,
        borderRadius: "5px",
      }}
    />
  );
};
LagChip.propTypes = { lag: PropTypes.number.isRequired };

// ─── expanded message row ─────────────────────────────────────────────────────

const ExpandedMessageRow = ({ data: row }) => {
  const msgText =
    row.msg && typeof row.msg === "object"
      ? JSON.stringify(row.msg, null, 2)
      : String(row.msg ?? "—");

  const headersText =
    row.headers && typeof row.headers === "object" ? JSON.stringify(row.headers, null, 2) : null;

  return (
    <SoftBox
      sx={{
        px: 3,
        py: 2.5,
        background: "rgba(94,114,228,0.03)",
        borderTop: "1px solid rgba(0,0,0,0.05)",
        borderBottom: "1px solid rgba(0,0,0,0.05)",
      }}
    >
      <Grid container spacing={2}>
        {/* full message */}
        <Grid item xs={12} md={headersText ? 8 : 12}>
          <SoftTypography
            variant="caption"
            fontWeight="bold"
            color="secondary"
            sx={{ display: "block", mb: 0.8, textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            Full Message
          </SoftTypography>
          <SoftBox
            component="pre"
            sx={{
              m: 0,
              p: 2,
              background: "#1a1f35",
              color: "#a8b4d8",
              borderRadius: "10px",
              fontSize: "0.72rem",
              fontFamily: "monospace",
              lineHeight: 1.65,
              overflowX: "auto",
              maxHeight: 320,
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {msgText}
          </SoftBox>
        </Grid>

        {/* headers — only when present */}
        {headersText && (
          <Grid item xs={12} md={4}>
            <SoftTypography
              variant="caption"
              fontWeight="bold"
              color="secondary"
              sx={{ display: "block", mb: 0.8, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Headers
            </SoftTypography>
            <SoftBox
              component="pre"
              sx={{
                m: 0,
                p: 2,
                background: "#1a1f35",
                color: "#a8b4d8",
                borderRadius: "10px",
                fontSize: "0.72rem",
                fontFamily: "monospace",
                lineHeight: 1.65,
                overflowX: "auto",
                maxHeight: 320,
                overflowY: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {headersText}
            </SoftBox>
          </Grid>
        )}

        {/* meta strip */}
        <Grid item xs={12}>
          <SoftBox display="flex" flexWrap="wrap" gap={3}>
            {[
              { label: "Topic", value: row.topic },
              { label: "Partition", value: row.partition },
              { label: "Offset", value: row.offset },
              {
                label: "Timestamp",
                value: row.timestamp ? new Date(row.timestamp).toLocaleString() : "—",
              },
              { label: "Key", value: row.key || "—" },
            ].map(({ label, value }) => (
              <SoftBox key={label}>
                <SoftTypography
                  variant="caption"
                  color="secondary"
                  sx={{
                    display: "block",
                    fontWeight: 600,
                    fontSize: "0.68rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    mb: 0.3,
                  }}
                >
                  {label}
                </SoftTypography>
                <SoftTypography
                  variant="caption"
                  fontWeight="medium"
                  sx={{ fontFamily: "monospace", fontSize: "0.78rem" }}
                >
                  {String(value ?? "—")}
                </SoftTypography>
              </SoftBox>
            ))}
          </SoftBox>
        </Grid>
      </Grid>
    </SoftBox>
  );
};
ExpandedMessageRow.propTypes = { data: PropTypes.object.isRequired };

// ─── main component ───────────────────────────────────────────────────────────

function KafkaEventDetails() {
  const { dark } = colors;
  const { t } = useTranslation();
  const { topicName } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { api } = useAPI();
  const { errorState, showAlert } = useAlert();

  // stats state
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // messages state
  const [tabValue, setTabValue] = useState(0); // 0=processed, 1=pending
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState({});
  const [msgLoading, setMsgLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");

  if (!hasPermission("can_view_kafka")) return <Unauthorized />;

  const decodedTopic = topicName ? decodeURIComponent(topicName) : "";

  // ─── fetch stats ─────────────────────────────────────────────────────────

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get(`/topics/${encodeURIComponent(decodedTopic)}/`);
      setStats(res.data);
    } catch (err) {
      showAlert("error", err?.response?.data?.message || t("lang.something_went_wrong"));
    } finally {
      setStatsLoading(false);
    }
  }, [decodedTopic]);

  useEffect(() => {
    if (decodedTopic) fetchStats();
  }, [decodedTopic]);

  // ─── fetch messages ───────────────────────────────────────────────────────

  const fetchMessages = useCallback(async () => {
    setMsgLoading(true);
    const tab = tabValue === 0 ? "processed" : "pending";
    try {
      const res = await api.get(`/topics/${encodeURIComponent(decodedTopic)}/messages/`, {
        params: { tab, page, per_page: perPage },
      });
      setMessages(res.data.messages || []);
      setPagination(res.data.pagination || {});
    } catch (err) {
      showAlert("error", err?.response?.data?.message || t("lang.something_went_wrong"));
    } finally {
      setMsgLoading(false);
    }
  }, [decodedTopic, tabValue, page, perPage]);

  useEffect(() => {
    if (decodedTopic) fetchMessages();
  }, [fetchMessages]);

  // ─── handlers ────────────────────────────────────────────────────────────

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    setPage(1);
    setMessages([]);
    setSearch("");
  };

  const handlePageChange = (newPage) => setPage(newPage);
  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  // client-side search on message key / value
  const filteredMessages = search
    ? messages.filter((m) => {
        const haystack = `${m.key ?? ""} ${JSON.stringify(m.msg ?? "")}`.toLowerCase();
        return haystack.includes(search.toLowerCase());
      })
    : messages;

  // ─── message table columns ────────────────────────────────────────────────

  const msgColumns = [
    {
      name: (
        <SoftTypography variant="caption" fontWeight="bold" color="secondary">
          Partition
        </SoftTypography>
      ),
      selector: (row) => row.partition,
      sortable: true,
      width: "10%",
      cell: (row) => (
        <SoftTypography variant="caption" fontWeight="medium">
          {row.partition}
        </SoftTypography>
      ),
    },
    {
      name: (
        <SoftTypography variant="caption" fontWeight="bold" color="secondary">
          Offset
        </SoftTypography>
      ),
      selector: (row) => row.offset,
      sortable: true,
      width: "12%",
      cell: (row) => (
        <SoftTypography variant="caption" fontWeight="medium">
          {row.offset}
        </SoftTypography>
      ),
    },
    {
      name: (
        <SoftTypography variant="caption" fontWeight="bold" color="secondary">
          Timestamp
        </SoftTypography>
      ),
      selector: (row) => row.timestamp,
      width: "20%",
      cell: (row) => (
        <SoftTypography variant="caption" color="secondary">
          {row.timestamp ? new Date(row.timestamp).toLocaleString() : "—"}
        </SoftTypography>
      ),
    },
    {
      name: (
        <SoftTypography variant="caption" fontWeight="bold" color="secondary">
          Key
        </SoftTypography>
      ),
      selector: (row) => row.key,
      width: "18%",
      cell: (row) => (
        <SoftTypography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
          {row.key || "—"}
        </SoftTypography>
      ),
    },
    {
      name: (
        <SoftTypography variant="caption" fontWeight="bold" color="secondary">
          Message
        </SoftTypography>
      ),
      selector: (row) => row.msg,
      grow: 1,
      cell: (row) => {
        const txt =
          row.msg && typeof row.msg === "object" ? JSON.stringify(row.msg) : String(row.msg ?? "—");
        return (
          <Tooltip title={txt} placement="top">
            <SoftTypography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 280,
                display: "block",
                cursor: "default",
              }}
            >
              {txt}
            </SoftTypography>
          </Tooltip>
        );
      },
    },
    {
      name: (
        <SoftTypography variant="caption" fontWeight="bold" color="secondary">
          Status
        </SoftTypography>
      ),
      width: "12%",
      cell: (row) => (
        <Chip
          label={row.status === "processed" ? "Processed" : "Pending"}
          size="small"
          sx={{
            background:
              row.status === "processed" ? "rgba(45,206,137,0.12)" : "rgba(251,99,64,0.12)",
            color: row.status === "processed" ? "#1aae6f" : "#fb6340",
            fontWeight: 600,
            fontSize: "0.7rem",
            height: 22,
            borderRadius: "5px",
          }}
        />
      ),
    },
  ];

  // ─── partition table rows ─────────────────────────────────────────────────

  const partitionRows = stats
    ? Object.entries(stats.partition_details || {}).map(([key, val]) => ({
        partition: key.replace("partition_", "P"),
        consumer_offset: val.consumer_offset,
        high_water_mark: val.high_water_mark,
        lag: val.lag,
      }))
    : [];

  const pagesList = [
    { name: t("lang.topics"), route: "/topics" },
    { name: decodedTopic, route: "" },
  ];

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <DashboardNavbar pagesList={pagesList} />

      {/* ── page header ── */}
      <SoftBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2.5}
        flexWrap="wrap"
        gap={1.5}
      >
        <SoftBox display="flex" alignItems="center" gap={1.5}>
          <SoftBox
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: "linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <TopicIcon sx={{ color: "#fff", fontSize: 20 }} />
          </SoftBox>
          <SoftBox>
            <SoftBox display="flex" alignItems="center" gap={1}>
              <SoftTypography
                variant="h6"
                fontWeight="bold"
                sx={{ fontFamily: "monospace", letterSpacing: 0.5 }}
              >
                {decodedTopic}
              </SoftTypography>
              {!statsLoading && stats && <StatusBadge status={stats.status} />}
            </SoftBox>
            {!statsLoading && stats && (
              <SoftTypography variant="caption" color="secondary">
                Consumer group: <strong>{stats.consumer_group}</strong>
              </SoftTypography>
            )}
          </SoftBox>
        </SoftBox>

        <SoftBox display="flex" alignItems="center" gap={1}>
          <Tooltip title="Refresh stats" placement="top">
            <IconButton
              onClick={fetchStats}
              disabled={statsLoading}
              sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "10px", p: 1 }}
            >
              {statsLoading ? (
                <CircularProgress size={18} thickness={4} />
              ) : (
                <RefreshIcon sx={{ fontSize: 20, color: "#5e72e4" }} />
              )}
            </IconButton>
          </Tooltip>
          <SoftButton
            variant="outlined"
            color="dark"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/topics")}
            sx={{ borderRadius: "10px", px: 2.5 }}
          >
            {t("lang.actions.back")}
          </SoftButton>
        </SoftBox>
      </SoftBox>

      {/* ── stat cards ── */}
      <Grid container spacing={2.5} mb={3}>
        {[
          {
            icon: <MessageIcon />,
            label: "Total Messages",
            value: stats?.total_messages,
            color: "#5e72e4",
          },
          {
            icon: <CheckCircleOutlineIcon />,
            label: "Processed",
            value: stats?.processed,
            color: "#2dce89",
          },
          {
            icon: <HourglassEmptyIcon />,
            label: "Pending (Lag)",
            value: stats?.pending,
            color: stats?.pending > 0 ? "#fb6340" : "#2dce89",
          },
          {
            icon: <LayersIcon />,
            label: "Partitions",
            value: stats?.partitions,
            color: "#11cdef",
          },
        ].map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <StatCard {...card} loading={statsLoading} />
          </Grid>
        ))}
      </Grid>

      {/* ── partition details table ── */}
      <Card
        sx={{
          mb: 3,
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          borderRadius: "14px",
          overflow: "hidden",
        }}
      >
        <SoftBox px={3} py={2} display="flex" alignItems="center" gap={1.5}>
          <LayersIcon sx={{ fontSize: 18, color: "#5e72e4" }} />
          <SoftTypography variant="button" fontWeight="bold">
            Partition Breakdown
          </SoftTypography>
        </SoftBox>
        <Divider sx={{ m: 0, borderColor: "rgba(0,0,0,0.06)" }} />
        {statsLoading ? (
          <SoftBox px={3} py={3} display="flex" flexDirection="column" gap={1.2}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={36} sx={{ borderRadius: "8px" }} />
            ))}
          </SoftBox>
        ) : partitionRows.length === 0 ? (
          <SoftBox px={3} py={4} textAlign="center">
            <SoftTypography variant="body2" color="secondary">
              No partition data available.
            </SoftTypography>
          </SoftBox>
        ) : (
          <SoftBox sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(0,0,0,0.02)" }}>
                  {["Partition", "Consumer Offset", "High Water Mark", "Lag", "Status"].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 20px",
                        textAlign: "left",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "#8392ab",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {partitionRows.map((row, i) => (
                  <tr
                    key={row.partition}
                    style={{
                      background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                      borderBottom: "1px solid rgba(0,0,0,0.04)",
                    }}
                  >
                    <td style={{ padding: "10px 20px" }}>
                      <Chip
                        label={row.partition}
                        size="small"
                        sx={{
                          background: "rgba(94,114,228,0.1)",
                          color: "#5e72e4",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          height: 22,
                          borderRadius: "5px",
                          fontFamily: "monospace",
                        }}
                      />
                    </td>
                    <td style={{ padding: "10px 20px" }}>
                      <SoftTypography variant="caption" fontWeight="medium">
                        {row.consumer_offset.toLocaleString()}
                      </SoftTypography>
                    </td>
                    <td style={{ padding: "10px 20px" }}>
                      <SoftTypography variant="caption" fontWeight="medium">
                        {row.high_water_mark.toLocaleString()}
                      </SoftTypography>
                    </td>
                    <td style={{ padding: "10px 20px" }}>
                      <LagChip lag={row.lag} />
                    </td>
                    <td style={{ padding: "10px 20px" }}>
                      <Chip
                        label={row.lag === 0 ? "Caught Up" : "Lagging"}
                        size="small"
                        sx={{
                          background:
                            row.lag === 0 ? "rgba(45,206,137,0.1)" : "rgba(245,54,92,0.1)",
                          color: row.lag === 0 ? "#2dce89" : "#f5365c",
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          height: 22,
                          borderRadius: "5px",
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SoftBox>
        )}
      </Card>

      {/* ── messages card ── */}
      <Card
        sx={{
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          borderRadius: "14px",
          overflow: "hidden",
        }}
      >
        {/* tab bar + search */}
        <SoftBox
          px={3}
          py={1.5}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            TabIndicatorProps={{ sx: { backgroundColor: dark.main } }}
          >
            <Tab
              label="Processed Messages"
              sx={{
                textTransform: "none",
                "&.Mui-selected": { color: dark.main },
                fontSize: "0.82rem",
              }}
            />
            <Tab
              label="Pending Messages"
              sx={{
                textTransform: "none",
                "&.Mui-selected": { color: dark.main },
                fontSize: "0.82rem",
              }}
            />
          </Tabs>

          <SoftInput
            placeholder="Search messages…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            icon={{ component: <SearchIcon sx={{ fontSize: 17 }} />, direction: "left" }}
            sx={{ width: 220 }}
          />
        </SoftBox>

        <Divider sx={{ m: 0, borderColor: "rgba(0,0,0,0.06)" }} />

        <SoftBox p={2}>
          <DataTable
            columns={msgColumns}
            data={filteredMessages}
            progressPending={msgLoading}
            progressComponent={
              <SoftBox py={4} display="flex" justifyContent="center">
                <CircularProgress size={28} thickness={4} sx={{ color: "#5e72e4" }} />
              </SoftBox>
            }
            expandableRows
            expandableRowsComponent={ExpandedMessageRow}
            pagination
            paginationServer
            paginationTotalRows={pagination.total_messages ?? 0}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerPageChange}
            highlightOnHover
            noDataComponent={
              <SoftBox py={5} display="flex" flexDirection="column" alignItems="center" gap={1}>
                <MessageIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.18)" }} />
                <SoftTypography variant="body2" color="secondary">
                  {search
                    ? "No messages match your search."
                    : tabValue === 0
                    ? "No processed messages found."
                    : "No pending messages — all caught up!"}
                </SoftTypography>
              </SoftBox>
            }
          />
        </SoftBox>
      </Card>

      <AlertMessage {...errorState} />
    </DashboardLayout>
  );
}

export default KafkaEventDetails;
